import React, { useCallback, useEffect } from "react";
import * as Sentry from "@sentry/react";

// Third-party Libraries
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Redux Slices
import { RootState, AppDispatch } from "./store/indexRedux";
import { fetchUserCompany, setCompanyFromCache } from "./store/companySlice";
import { fetchUserInfo } from "./store/userSlice";
import { fetchPortfolioServices, selectPortfolioServices } from "./store/companyPortfolioSlice";

// import { validateToken } from './store/authSlice';
// import { fetchInstallations } from './store/installationSlice';

// API Calls
import { fetchPortalsDocumentUrl } from "./api/documents";

// Components
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Homepage from "./pages/HomePage";
import Root from "./pages/Root";
import Error from "./pages/ErrorPage";
import Login from "./pages/LoginPage";
import Services from "./pages/ActiveServicePage";
import Document from "./pages/DocumentPage";
import ViewDocuments from "./pages/ViewDocumentPage";

// Styles
import "./App.css";
import { useTheme } from "./hooks/useTheme";
import SelectCompanyPage from "./pages/SelectCompanyPage";
// import { getSessionFromCookie } from './components/utils/cognito';
import { jwtDecode } from "jwt-decode";
import { setAccessToken, setDecodedToken } from "./store/authv2Slice";
import { getSessionFromCookie } from "./components/utils/cognito";
import AddCompanyPage from "./pages/AddCompanyPage";
import { fetchPendingBessResignsThunk } from "./store/bessResignSlice";
import CompanyProtectedRoute from "./components/CompanyProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import ServiceDashboardPage from "./pages/ServiceDashboardPage";
import Start from "./pages/Start";
import AddService from "./pages/AddService";
import GeneratePartnerCoupon from "./pages/GeneratePartnerCoupon";
import TermsAndConditionsViewer from "./components/documentsComp/TermsAndConditionViewer";
import PvCurltaiment from "./pages/PvCurltaiment";
import DeletePvCurtailment from "./pages/DeletePvCurtailment";
import BessOnboarding from "./pages/BessOnboarding";
import ManagementPage from "./pages/ManagementPage";
import FluviusManager from "./components/management/FluviusManager";
import BessResignAdmin from "./components/management/BessResignAdmin";
import EndContractManager from "./components/management/EndContractManager";
import SignContractPage from "./pages/SignContractPage";
import SignBessContractPage from "./pages/SignBessContractPage";
import SignCommodityContractPage from "./pages/SignCommodityContractPage";
// import { fetchCompanyServices } from './store/companyServiceSlice';

interface DecodedToken {
  sub: string;
  exp: number;
  username: string;
  // Add other properties you need from the token
}

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const session = getSessionFromCookie();
  const token = session?.getAccessToken().getJwtToken();
  console.log(token);
  const isJwtToken = (token: string): boolean => {
    try {
      // Attempt to decode the token using jwtDecode
      jwtDecode(token);
      return true; // If successful, it's a JWT
    } catch {
      return false; // If decoding fails, it's not a JWT
    }
  };

  let cognitoId: string | undefined;
  if (token && isJwtToken(token)) {
    try {
      const decodedToken: { sub: string } = jwtDecode(token);
      cognitoId = decodedToken.sub;
    } catch (error) {
      console.error("Error decoding JWT token:", error);
    }
  }

  const selectedUserCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);
  // const token = useSelector((state: RootState) => state.auth.session?.accessToken);
  // const cognitoId = useSelector((state: RootState) => state.auth.cognitoId);

  useEffect(() => {
    if (token) {
      dispatch(setAccessToken(token));
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        dispatch(setDecodedToken(decoded));
      } catch (error) {
        console.error("Error decoding JWT token:", error);
      }

      dispatch(fetchUserInfo({ token }));
    }
  }, [dispatch, token]);

  useTheme();
  // useEffect(() => {
  //   if (token) {
  //     dispatch(validateToken());
  //   }
  // }, [dispatch, token]);

  const services = useSelector(selectPortfolioServices);
  console.log(services);

  useEffect(() => {
    const selectedCompanyId = localStorage.getItem("selectedCompanyId");
    if (selectedCompanyId && token) {
      const cachedCompany = localStorage.getItem("selectedUserCompany");
      if (cachedCompany) {
        const companyData = JSON.parse(cachedCompany);
        dispatch(setCompanyFromCache(companyData));
      } else {
        dispatch(fetchUserCompany({ company_id: selectedCompanyId, token }));
      }
    }
  }, [dispatch, token]);

  useEffect(() => {
    const selectedCompanyId = localStorage.getItem("selectedCompanyId");
    if (selectedCompanyId && token) {
      const cachedCompany = localStorage.getItem("selectedUserCompany");
      if (cachedCompany) {
        const companyData = JSON.parse(cachedCompany);
        dispatch(setCompanyFromCache(companyData));
      } else {
        dispatch(fetchUserCompany({ company_id: selectedCompanyId, token }));
      }
    }
  }, [dispatch, token]);

  const fetchData = useCallback(() => {
    if (token && cognitoId && selectedUserCompany?.vatCode) {
      dispatch(
        fetchPortfolioServices({
          companyNumber: selectedUserCompany.companyNumber,
          token,
        }),
      );
      if (selectedUserCompany.company_id != null) {
        dispatch(fetchPendingBessResignsThunk({ companyId: selectedUserCompany.company_id, token }));
      }
    }
  }, [dispatch, cognitoId, token, selectedUserCompany]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

  const router = sentryCreateBrowserRouter([
    {
      path: "/",
      element: <Root />,
      errorElement: <Error />,
      children: [
        {
          path: "login",
          element: <Login />,
        },
        {
          path: "sign/:token",
          element: <SignContractPage />,
        },
        {
          path: "sign-bess/:token",
          element: <SignBessContractPage />,
        },
        {
          path: "sign-commodity/:token",
          element: <SignCommodityContractPage />,
        },
        {
          element: <ProtectedRoute />, // Ensures user is authenticated
          children: [
            {
              index: true,
              element: <SelectCompanyPage />, // User can see this page even without a company selected
            },
            {
              path: "add-company",
              element: <AddCompanyPage />,
            },
            {
              element: <CompanyProtectedRoute />, // Ensures a company is selected
              children: [
                {
                  path: "overview",
                  element: <Homepage />,
                },
                { path: "start", element: <Start /> },
                {
                  path: "services",
                  element: <Services />,
                },
                {
                  path: "services/:serviceId/:type",
                  element: <ServiceDashboardPage />,
                },
                {
                  path: "documents",
                  element: <Document />,
                },
                {
                  path: "addservice",
                  element: <AddService />,
                },
                { path: "pv-curtailment", element: <PvCurltaiment /> },
                { path: "pv-curtailment/delete", element: <DeletePvCurtailment /> },
                { path: "bess", element: <BessOnboarding /> },
                // Open to every authenticated user with a company selected.
                // The management landing page itself gates the admin-only
                // cards, and End Contract is scoped BE-side by
                // verifyUserHasAccessToCompanyByEmail — a user can only ever
                // end contracts of a company their own contact row is linked
                // to. Admin-only date freedom is resolved server-side from the
                // JWT (isAdminContact), never from the client.
                { path: "management", element: <ManagementPage /> },
                { path: "management/end-contract", element: <EndContractManager /> },
                {
                  element: <AdminProtectedRoute />, // Ensures the user is an admin
                  children: [
                    { path: "management/fluvius", element: <FluviusManager /> },
                    { path: "management/bess-resign", element: <BessResignAdmin /> },
                  ],
                },
                {
                  path: "generatecoupon",
                  element: <GeneratePartnerCoupon />,
                },

                {
                  path: "documents/:documentId",
                  element: (
                    <ViewDocuments
                      fetchDocument={(kbo, doc) => fetchPortalsDocumentUrl(kbo, doc, 'completed_agreement')}
                      backLink="/documents"
                      title="Document Agreement"
                    />
                  ),
                },
                {
                  path: "statements/:documentId",
                  element: (
                    <ViewDocuments
                      fetchDocument={(kbo, doc) => fetchPortalsDocumentUrl(kbo, doc, 'statement')}
                      backLink="/documents"
                      title="Document Statement"
                    />
                  ),
                },
                {
                  path: "mandates/:documentId",
                  element: (
                    <ViewDocuments
                      fetchDocument={(kbo, doc) => fetchPortalsDocumentUrl(kbo, doc, 'signed_mandate')}
                      backLink="/documents"
                      title="Document Statement"
                    />
                  ),
                },
                {
                  path: "switch/:documentId",
                  element: (
                    <ViewDocuments
                      fetchDocument={(kbo, doc) => fetchPortalsDocumentUrl(kbo, doc, 'switch_confirmation')}
                      backLink="/documents"
                      title="Switch Confirmation"
                    />
                  ),
                },
                {
                  path: "terms/:fileName",
                  element: <TermsAndConditionsViewer />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]);

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  );
};

export default App;
